import { DateTime } from "luxon";
import EscPosEncoder from "esc-pos-encoder";

import commands from "./commands";
import { User } from "../../models/user_model";
import { Branch } from "../../models/partner_model";
import { OrderType } from "../../models/order_model";
import { Settings } from "../../models/settings_model";
import { CashoutsType, CashMoveType } from "../../models/cashouts";
import num_to_text from "../../middleware/num_text";

function ticketPaymentLabel(payment_type: string, card_type?: string): string {
  let label = "";
  switch (payment_type) {
    case "cash":
      label = "Efectivo";
      break;
    case "terminal":
    case "card_pos":
      label = "Terminal";
      break;
    case "online":
      label = "En linea";
      break;
    default:
      label = payment_type;
  }
  if (card_type === "debit") label += " (Debito)";
  else if (card_type === "credit") label += " (Credito)";
  else if (card_type === "service") label += " (Servicios)";
  return label;
}

const encoder = new EscPosEncoder();

export function getOpenDrawerRaw() {
  const print_text = new EscPosEncoder().initialize();
  print_text.raw([0x1b, 0x70, 0x00, 0x19, 0xfa]);
  return {
    print_text: print_text.encode(),
    has_products: true,
  };
}

export function loadTicketLogo(
  url: string,
  width = 160,
  height = 160,
): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(null);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas);
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export type PrintType = "client" | "restaurant";

export async function getTicket(
  order: OrderType,
  settings: Settings,
  type: PrintType,
  re_print: boolean = false,
) {
  const user: User = JSON.parse(localStorage.getItem("user") || "{}");
  const branch: Branch = JSON.parse(localStorage.getItem("branch") || "{}");
  const { partner } = user;
  const restaurant_name = partner.company_name
    .replace(/[.*+?^${}()|[\]\\]/g, " ")
    .replace("%26", "&");
  let subtotal_text,
    tax_text,
    total_text,
    delivery_text,
    income_text,
    change_text;
  const restaurant_page = partner.url_name;
  const fiscal_data: string | boolean =
    settings.printer_fiscal && partner.fiscal_name
      ? partner.fiscal_name
      : false;
  let user_name = order.user
    ? `${order.user.name || ""} ${order.user.lastname || ""}`
    : `${user.name || ""} ${user.lastname || ""}`;
  const is_payed = order.payed || false;
  if (user_name.length >= 2) {
    user_name = commands.Line + "Atendido por " + user_name;
  } else {
    user_name = "";
  }

  const active_branch_info = branch || false;
  // const active_zone = settings.active_zone;
  const str = "" + order.id;
  const pad = "0000";
  const order_id_txt = "1" + pad.substring(0, pad.length - str.length) + str;
  const turn_txt = "#" + str.slice(-2); //gets last character;
  const total = order.total;
  const total_txt = num_to_text(total);
  const iva = total - total / 1.16;
  const subtotal = total - iva;
  let has_products: boolean = false;
  let dateTime = order.creation_date
    ? DateTime.fromMillis(Number(order.creation_date)).toLocaleString(
        DateTime.DATETIME_MED,
      )
    : DateTime.local();
  let delivery_info: string | boolean = false;
  let table_info: string | boolean = false;
  let delivery: string = "En Comedor";
  const products_zones: boolean = settings.printer_type === "network";

  switch (order.delivery_type) {
    case "for_here":
      delivery = "En Comedor";
      table_info = true;
      break;
    case "pickup":
      delivery = "Para Llevar";
      break;
    case "delivery":
      delivery = "A Domicilio";
      delivery_info = true;
      break;
    case "other_apps":
      delivery = order.platform_name
        ? `Plataformas - ${order.platform_name}`
        : "Plataformas";
      break;
    default:
      delivery = "En Comedor";
  }

  if (table_info && order.table && order.table.id) {
    table_info =
      commands.Line +
      `Mesa # ${order.table.name || order.table.id}` +
      commands.Line;
  } else {
    table_info = "";
  }

  const print_text = encoder.initialize().codepage("cp850");
  if (settings.printer_type === "network") {
    print_text.newline().newline().newline().cut("partial");
  }
  if (type === "client") {
    if (settings.ticket_logo) {
      const logoWidth = settings.ticket_logo_width || 160;
      const logoHeight = settings.ticket_logo_height || 160;
      try {
        const logo = await loadTicketLogo(settings.ticket_logo, logoWidth, logoHeight);
        if (logo) {
          print_text.align("center").image(logo, logoWidth, logoHeight).newline();
        }
      } catch (error) {
        console.log("getTicket.ts:108 | error", error);
      }
    }

    print_text
      .newline()
      .bold()
      .text(restaurant_name)
      .bold()
      .newline()
      .align("left");
  }

  //pring fiscal data if any
  if (type === "client" && fiscal_data && is_payed && partner.rfc) {
    print_text.line(fiscal_data).line(partner.rfc);
  }
  ////add address
  if (type === "client" && active_branch_info.address) {
    print_text.line(active_branch_info.address);
    print_text.line(active_branch_info.phone);
  }

  const compact_restaurant_copy =
    type === "restaurant" && !!settings.compact_restaurant_copy;

  if (order_id_txt) {
    if (type === "client") {
      print_text.align("right").line(`Orden #${order_id_txt}`);
    } else if (!compact_restaurant_copy) {
      print_text
        .align("center")
        .text(commands.DoubleOn)
        .line("#" + order_id_txt)
        .text(commands.DoubleOff)
        .line("");
    }
  }
  if ((order as any).split_bill_label) {
    print_text
      .align("center")
      .bold()
      .line((order as any).split_bill_label)
      .bold()
      .align("left");
  }
  print_text.line(`Fecha  ${dateTime}`);

  if (!compact_restaurant_copy) {
    const turn_text =
      commands.enter +
      commands.CenterOn +
      "Turno:" +
      commands.Line +
      commands.DoubleOn +
      turn_txt +
      commands.DoubleOff +
      commands.Line +
      commands.LeftOn;

    print_text.line(turn_text);
  }
  print_text
    .line(`${compact_restaurant_copy ? "" : user_name} ${table_info}`)
    .newline()
    .newline()
    .align("left");

  if (order.client_comments && order.client_comments !== true) {
    if (type === "client") {
      print_text.bold().line("Comentarios").bold();
      print_text.line(order.client_comments);
    } else {
      print_text.line(commands.h3On + "Comentarios " + commands.h3Off);
      print_text
        .line(commands.h3On + order.client_comments + commands.h3Off)
        .newline()
        .newline();
    }
  }
  if (order.user_comments && order.user_comments !== true) {
    if (type === "client") {
      print_text.bold().line("Comentarios del Ciente").bold();
      print_text.line(order.user_comments);
    } else {
      print_text.line(
        commands.h3On + "Comentarios del Cliente" + commands.h3Off,
      );
      print_text.line(commands.h3On + order.user_comments + commands.h3Off);
    }
  }

  const createdProducts = order.createdProducts ? order.createdProducts : [];
  const product_line = `Descripcion`.padEnd(32, " ") + `Importe`;
  print_text.line(product_line);

  // Group products by category
  const categoryMap: Record<string, { name: string; products: typeof order.products }> = {};
  order.products.forEach((product) => {
    const category = product.product?.category;
    const categoryId = category ? category.id : "other";
    const categoryName = category ? category.name : "Otros";
    if (!categoryMap[categoryId]) {
      categoryMap[categoryId] = { name: categoryName, products: [] };
    }
    categoryMap[categoryId].products.push(product);
  });

  Object.values(categoryMap).forEach(({ name: categoryName, products: categoryProducts }) => {
    let categoryPrinted = false;

    categoryProducts.forEach((product) => {
      let print_help = false;
      let size_text = "";
      let comment_text = "";
      let ing_text = "";
      let help_text = "";
      if (product.ingredients_txt) {
        print_help = true;
        ing_text = product.ingredients_txt
          .split(" | ")
          .filter((item) => item !== "" && item !== " ")
          .map((item) => "   -" + item + commands.Line)
          .join("");
      }
      if (product.size) {
        const size_data = product.product.sizes.find(
          (s) => s.id === product.size,
        );
        print_help = true;
        size_text = size_data ? size_data.name + ", " : "";
      }
      if (product.comments) {
        comment_text = " **" + product.comments;
      }
      if (print_help) {
        help_text = "   " + size_text;
        help_text += ing_text;
      }
      const product_print_price = product.price;
      if (type === "restaurant") {
        // Print only products from this batch (createdProducts) — everything
        // else was already printed in an earlier batch and, since
        // setAsReady stamps products ready at creation time rather than
        // after printing, may already carry status '1' even though it's
        // only now being printed for the first time. re_print bypasses this
        // for manual reprints (staff explicitly asked to reprint the whole
        // ticket, e.g. a paper jam) — those intentionally print everything.
        if (!createdProducts.includes(+product.id) && !re_print) {
          return;
        }
        if (!categoryPrinted) {
          print_text.line(commands.h3On + `-- ${categoryName} --` + commands.h3Off);
          categoryPrinted = true;
        }
        // On a manual reprint, flag products that are already marked ready
        // (the green checkmark in the UI) so kitchen staff don't mistake a
        // reprinted-but-already-made item for a new one to prepare. Some
        // restaurants don't want this — settings.print_ready_tag defaults
        // to on (undefined = on) but can be turned off per-partner.
        const isAlreadyReady =
          re_print &&
          settings.print_ready_tag !== false &&
          !createdProducts.includes(+product.id) &&
          product.status === "1";
        const readyTag = isAlreadyReady ? " (Listo)" : "";
        print_text.line(
          commands.h3On +
            `${product.product.title}${
              product.qty > 1 ? ` x${product.qty}` : ""
            }${readyTag} ${comment_text} ${help_text}` +
            commands.h3Off,
        );
      } else {
        if (!categoryPrinted) {
          print_text.line(`-- ${categoryName} --`);
          categoryPrinted = true;
        }
        // New table format for products and prices
        const product_line =
          `${product.product.title} ${
            product.qty > 1 ? ` x${product.qty}` : ""
          }`.padEnd(32, " ") +
          `${
            type === "client" || settings.price_ticket
              ? `$${product_print_price}`
              : ""
          }`;
        print_text.line(product_line);
        if (comment_text || help_text) {
          print_text.line(`${comment_text} ${help_text}`);
        }
      }
      has_products = true;
    });
  });

  if (delivery_info) {
    let delivery_info_text =
      commands.enter + "Costo Envio: $" + order.delivery.toPrecision(5);
    print_text.line(delivery_info_text);
    subtotal_text = commands.Line + "Subtotal: $" + subtotal.toPrecision(5);
  } else {
    subtotal_text = commands.enter + "Subtotal: $" + subtotal.toPrecision(5);
  }

  tax_text = commands.Line + "Iva: $" + iva.toPrecision(5);
  if (settings.printer_iva === false || compact_restaurant_copy) {
    tax_text = "";
    subtotal_text = "";
  }

  total_text = commands.Line + commands.RightOn;
  total_text +=
    commands.h3On +
    "Total: $" +
    order.total +
    commands.Line +
    commands.h3Off +
    total_txt;
  total_text += commands.LeftOn;
  delivery_text =
    commands.enter +
    commands.CenterOn +
    commands.DoubleOn +
    delivery +
    commands.DoubleOff +
    commands.Line +
    commands.LeftOn;
  if (type === "client" || settings.price_ticket) {
    print_text.line(subtotal_text + tax_text + total_text);
    if (order.tip && +order.tip > 0) {
      const tip_text =
        commands.Line + "Propina: $" + parseFloat(String(order.tip)).toFixed(2);
      print_text.line(tip_text);
    }
    if (order.payments && order.payments.length > 1) {
      print_text.line(commands.Line + "Pago dividido:");
      order.payments.forEach((p: any) => {
        const label = ticketPaymentLabel(p.payment_type, p.card_type);
        const amt = parseFloat(p.amount).toFixed(2);
        print_text.line("  " + label + ": $" + amt);
      });
    } else if (order.cash_income && order.payment_type === "cash") {
      const change = order.cash_income - order.total - (order.tip ? +order.tip : 0);
      income_text =
        commands.Line +
        "Efectivo Recibido: $" +
        order.cash_income.toPrecision(5);
      change_text = commands.Line + "Su Cambio: $" + change.toPrecision(5);
      print_text.line(income_text + change_text);
    } else if (order.payment_type === "terminal") {
      income_text = commands.Line + "Cobrado con Terminal";
      print_text.line(income_text);
    } else if (order.payment_type === "online") {
      income_text = commands.Line + "Pagado online desde FooApp";
      print_text.line(income_text);
    } else if (order.payment_type === "card_pos") {
      income_text = commands.Line + "Pagado online";
      print_text.line(income_text);
    }
  }

  print_text.line(delivery_text);

  if (delivery_info) {
    /// add delivery info to ticket
    let delivery_info_text = "";
    if (order.address && order.address !== true) {
      delivery_info_text =
        commands.enter +
        commands.BoldOn +
        "Dirección de entrega: " +
        commands.BoldOff +
        commands.Line +
        order.address.address +
        commands.Line +
        `${order.address.city ? `${order.address.city},` : ""}` +
        `${order.address.zip ? `${order.address.zip},` : ""}` +
        commands.Line +
        commands.BoldOn +
        "Comentarios:" +
        commands.BoldOff +
        commands.Line +
        order.address.comments;
    }
    delivery_info_text +=
      commands.Line +
      commands.BoldOn +
      "Cliente: " +
      commands.BoldOff +
      order.client.name +
      " " +
      order.client.lastname +
      commands.Line +
      commands.BoldOn +
      "Tel: " +
      commands.BoldOff +
      order.client.phone;
    print_text.line(delivery_info_text);
  }

  if (restaurant_page && type === "client") {
    print_text
      .newline()
      .line("Pide en commands.Linea en")
      .bold()
      .line(`${restaurant_page}.fooapp.mx`)
      .bold();
  }
  if (type === "client") {
    if (settings.ticket_txt) {
      print_text.newline().line(settings.ticket_txt);
    } else {
      print_text.newline().line("Gracias por su compra").line("Vuelva Pronto");
    }
  }

  if (!is_payed && type === "client") {
    print_text
      .newline()
      .line("*** Este ticket no es un ")
      .line("comprobante de pago ***");

    // QR code for online payment
    const has_stripe_connect = partner.stripe_connect_status === "active";
    if (has_stripe_connect && !settings.hide_payment_qr) {
      const payment_url = `https://fooapp.mx/pay/${partner.id}/${order.id}`;
      print_text
        .newline()
        .align("center")
        .qrcode(payment_url, 2, 6, "m")
        .newline()
        .line("Escanea para pagar en linea")
        .align("left");
    }
  }
  if (settings.printer_iva === false && type === "client") {
    print_text.line("*Precios no incluyen IVA*");
  }
  if (type === "client") {
    print_text.newline().line("** fooapp.mx **");
  } else {
    if (compact_restaurant_copy) {
      print_text
        .newline()
        .align("center")
        .line(`Orden #${order_id_txt}   Turno ${turn_txt}`)
        .align("left");
    }
    print_text.newline().line("Copia del Negocio");
  }
  print_text.newline().newline().newline().newline().newline();

  if (products_zones) {
    ////TODO validate product zones on tickets
  }
  // "Abrir Caja de Monedas al Cobrar" is triggered explicitly via
  // printer.openDrawer() from the charge-completion callers (payOrder,
  // finalizeSplit, finalizeBillSplit in order/index.js) when the payment
  // involved cash, independent of whether a client ticket is printed here.
  // Do not also open it from this ticket build — that would double-fire
  // the drawer for any merchant who also has price_ticket/printer_autprint
  // enabled.
  return {
    print_text: print_text.cut("partial").encode(),
    has_products,
  };
}

export function getCashcutTicket(
  cashouts: CashoutsType,
  moves: CashMoveType[],
  settings: Settings,
  date: string,
) {
  const user: User = JSON.parse(localStorage.getItem("user") || "{}");
  const branch: Branch = JSON.parse(localStorage.getItem("branch") || "{}");
  const { partner } = user;
  const restaurant_name = partner.company_name
    .replace(/[.*+?^${}()|[\]\\]/g, " ")
    .replace("%26", "&");
  const separator = "----------------------------------------";

  const print_text = new EscPosEncoder().initialize().codepage("cp850");

  // Header
  print_text.align("center");
  print_text.bold().line(restaurant_name).bold();

  if (branch?.address) {
    print_text.line(branch.address);
  }

  print_text.newline();
  print_text
    .text(commands.DoubleOn)
    .line("CORTE DE CAJA")
    .text(commands.DoubleOff);

  // Date and user info
  print_text.newline().align("left");
  print_text.line(`Fecha:    ${date}`);
  print_text.line(
    `Impreso:  ${DateTime.local().toLocaleString(DateTime.DATETIME_MED)}`,
  );
  const userName = `${user.name || ""} ${user.lastname || ""}`.trim();
  if (userName) {
    print_text.line(`Cajero:   ${userName}`);
  }
  const branchName = branch?.name || branch?.address || "";
  if (branchName) {
    print_text.line(`Sucursal: ${branchName}`);
  }

  print_text.newline().line(separator).newline();

  // Summary section
  print_text.align("center").bold().line("RESUMEN").bold();
  print_text.align("left").newline();

  const summaryLine = (label: string, amount: number, negative = false) => {
    const sign = negative && amount > 0 ? "-" : "";
    const formatted = `${sign}$${amount.toFixed(2)}`;
    print_text.line(label.padEnd(28, " ") + formatted);
  };

  summaryLine("Entradas de efectivo", cashouts.in);
  summaryLine("Ventas en efectivo", cashouts.sales_cash || 0);
  summaryLine("Ventas con terminal", cashouts.sales_terminal || 0);
  if (cashouts.sales_platform) {
    summaryLine("Ventas en plataformas", cashouts.sales_platform);
  }
  if (cashouts.tips) {
    summaryLine("Propinas", cashouts.tips);
  }
  summaryLine("Salidas de efectivo", cashouts.out, true);
  print_text.newline().line(separator);

  const totalFormatted = `$${cashouts.total.toFixed(2)}`;
  print_text
    .bold()
    .line("TOTAL EN CAJA".padEnd(28, " ") + totalFormatted)
    .bold();

  print_text.line(separator).newline();

  // Sales breakdown
  const totalSales =
    (cashouts.sales_cash || 0) +
    (cashouts.sales_terminal || 0) +
    (cashouts.sales_platform || 0);
  print_text.align("center").bold().line("VENTAS DEL DIA").bold();
  print_text.align("left").newline();

  summaryLine("Total ventas", totalSales);
  summaryLine("  Efectivo", cashouts.sales_cash || 0);
  summaryLine("  Terminal", cashouts.sales_terminal || 0);
  if (cashouts.sales_platform) {
    summaryLine("  Plataformas", cashouts.sales_platform);
  }

  // Movements detail
  if (moves.length > 0) {
    print_text.newline().line(separator).newline();
    print_text.align("center").bold().line("MOVIMIENTOS").bold();
    print_text.align("left").newline();

    const moveTypeLabel = (type: string) => {
      switch (type) {
        case "in":
          return "Entrada";
        case "out":
          return "Salida";
        case "closed":
          return "Corte";
        case "started":
          return "Inicio";
        default:
          return type;
      }
    };

    for (const move of moves) {
      const typeLabel = moveTypeLabel(move.type);
      const sign = move.type === "out" || move.type === "closed" ? "-" : "+";
      const moveDate = move.creation_date
        ? DateTime.fromMillis(Number(move.creation_date)).toLocaleString(
            DateTime.TIME_SIMPLE,
          )
        : "";
      print_text.line(
        `${typeLabel}`.padEnd(12, " ") +
          `${sign}$${move.amount.toFixed(2)}`.padEnd(16, " ") +
          moveDate,
      );
      if (move.concept) {
        print_text.line(`  ${move.concept}`);
      }
    }
  }

  // Footer
  print_text.newline().line(separator);
  print_text.align("center").newline().line("** fooapp.mx **");
  print_text.newline().newline().newline().newline().newline();

  if (settings.open_drawer) {
    print_text.raw([0x1b, 0x70, 0x00, 0x19, 0xfa]);
  }

  return {
    print_text: print_text.cut("partial").encode(),
    has_products: true,
  };
}
